import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormInputField,
  FormMultiSelect,
  FormSelectField,
  type SelectOption,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  IAddUserRequest,
  ICompanyApplicationTenant,
  ITenantInstanceInfo,
  IUpdateUserRequest,
  IUserInfo,
} from "@models";
import {
  STANDARD_USERS_KEY,
  useAddUser,
  useApplicationTenants,
  useHubCheckLoginId,
  useLanguages,
  useTimezones,
  useUpdateUser,
  useUserInfo,
  withCurrentRegionalOption,
} from "@query";
import { APP_BASE_PATH } from "@shared";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const createAddUserFormSchema = (t: (key: string) => string, isEdit: boolean) =>
  z.object({
    firstName: z.string().min(1, t("hub.administer.add_user_required")),
    lastName: z.string().min(1, t("hub.administer.add_user_required")),
    email: z
      .string()
      .min(1, t("hub.administer.add_user_required"))
      .email(t("hub.administer.add_user_email_invalid")),
    loginId: isEdit
      ? z.string().optional()
      : z.string().min(1, t("hub.administer.add_user_required")),
    role: z.string().min(1, t("hub.administer.add_user_required")),
    timezone: z.string().min(1, t("hub.administer.add_user_required")),
    language: z.string().min(1, t("hub.administer.add_user_required")),
    application: z.string().min(1, t("hub.administer.add_user_required")),
    tenant: z.array(z.string()).optional(),
  });

type AddUserFormData = z.infer<ReturnType<typeof createAddUserFormSchema>>;

const DEFAULT_ADD_USER_FORM_VALUES: AddUserFormData = {
  firstName: "",
  lastName: "",
  email: "",
  loginId: "",
  role: "",
  timezone: "UTC",
  language: "en-US",
  application: "",
  tenant: [],
};

const ROLE_OPTIONS: SelectOption[] = [
  { value: "admin", label: "Admin" },
  { value: "standard", label: "Standard User" },
];

const ROLE_TO_USER_TYPE: Record<string, string> = {
  admin: "COMPANY_ADMIN",
  standard: "STANDARD_USER",
  app_admin: "APP_ADMIN",
};

const USER_TYPE_TO_ROLE: Record<string, string> = {
  COMPANY_ADMIN: "admin",
  STANDARD_USER: "standard",
  APP_ADMIN: "app_admin",
};

function buildAddUserRequest(
  data: AddUserFormData,
  companyUuid: string,
  createdBy: string,
  applicationTenantsData: ICompanyApplicationTenant[] | undefined,
): IAddUserRequest {
  const app = applicationTenantsData?.find(
    (a) => a.applicationVersionId === data.application,
  );

  const tenantInstanceIds = data.tenant?.length
    ? data.tenant
    : [app?.tenantInfo?.[0]?.tenantInstanceId || ""];

  const applicationTenants = tenantInstanceIds
    .map((tenantInstanceId) => {
      const tenantInfo =
        app?.tenantInfo?.find((t) => t.tenantInstanceId === tenantInstanceId) ??
        app?.tenantInfo?.[0];

      if (tenantInfo && app) {
        return {
          compAppMapId: tenantInfo.compAppMapId,
          applicationVersionId: app.applicationVersionId,
          tenantInstanceId: tenantInfo.tenantInstanceId,
        };
      }
      return null;
    })
    .filter((x): x is NonNullable<typeof x> => Boolean(x));

  return {
    companyUuid,
    email: data.email,
    loginId: data.loginId ?? "",
    userType: ROLE_TO_USER_TYPE[data.role] ?? "STANDARD_USER",
    firstName: data.firstName,
    lastName: data.lastName,
    status: "ACTIVE",
    language: data.language,
    timezone: data.timezone,
    phone: "",
    createdBy,
    lastModifiedBy: createdBy,
    applicationTenants,
  };
}

function buildUpdateUserRequest(
  data: AddUserFormData,
  user: IUserInfo,
  applicationTenantsData: ICompanyApplicationTenant[] | undefined,
): IUpdateUserRequest {
  const app = applicationTenantsData?.find(
    (a) => a.applicationVersionId === data.application,
  );

  const tenantInstanceIds = data.tenant?.length
    ? data.tenant
    : [app?.tenantInfo?.[0]?.tenantInstanceId || ""];

  const applicationTenants = tenantInstanceIds
    .map((tenantInstanceId) => {
      const tenantInfo =
        app?.tenantInfo?.find((t) => t.tenantInstanceId === tenantInstanceId) ??
        app?.tenantInfo?.[0];

      const existingUserTenant = user.applicationTenants
        ?.flatMap((at) => at.userTenants ?? [])
        .find(
          (ut) =>
            ut.tenantInstanceId === tenantInstanceId &&
            (tenantInfo ? ut.compAppMapId === tenantInfo.compAppMapId : true),
        );

      if (tenantInfo && app) {
        return {
          id: existingUserTenant?.id,
          compAppMapId: tenantInfo.compAppMapId,
          applicationVersionId: app.applicationVersionId,
          tenantInstanceId: tenantInfo.tenantInstanceId,
        };
      }
      return null;
    })
    .filter((x): x is NonNullable<typeof x> => Boolean(x));

  return {
    firstName: data.firstName,
    lastName: data.lastName,
    language: data.language,
    timezone: data.timezone,
    userType: ROLE_TO_USER_TYPE[data.role] ?? "STANDARD_USER",
    email: data.email,
    id: user.id,
    applicationTenants,
  };
}

interface IAddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyUuid?: string;
  user?: IUserInfo | null;
  onAddUser?: (data: AddUserFormData) => void;
}

function buildApplicationOptions(
  data: ICompanyApplicationTenant[] | undefined,
): SelectOption[] {
  if (!data?.length) return [];
  return data.map((app) => {
    const name = app.applicationLabel || app.applicationName;
    const version = app.applicationVersion?.trim();
    const label = version ? `${name} ${version}` : name;

    return {
      value: app.applicationVersionId,
      label,
    };
  });
}

function buildTenantOptions(
  data: ICompanyApplicationTenant[] | undefined,
  applicationVersionId: string,
): SelectOption[] {
  if (!data?.length || !applicationVersionId) return [];
  const app = data.find((a) => a.applicationVersionId === applicationVersionId);
  if (!app?.tenantInfo?.length) return [];
  return app.tenantInfo.map((t) => ({
    value: t.tenantInstanceId,
    label: t.tenantInstanceName,
  }));
}

// biome-ignore lint/suspicious/noExplicitAny: generic wrapper
function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export function AddUserDialog({
  open,
  onOpenChange,
  companyUuid,
  user,
  onAddUser,
}: IAddUserDialogProps) {
  const isEdit = !!user;
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const addUserFormSchema = useMemo(
    () => createAddUserFormSchema(t, isEdit),
    [t, isEdit],
  );
  const { mutateAsync: checkLoginId } = useHubCheckLoginId(
    companyUuid,
    APP_BASE_PATH,
  );
  const { mutate: addUser, isPending: isAddUserPending } = useAddUser(
    companyUuid,
    APP_BASE_PATH,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [STANDARD_USERS_KEY] });
      },
    },
  );
  const { mutate: updateUser, isPending: isUpdateUserPending } = useUpdateUser(
    companyUuid,
    APP_BASE_PATH,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [STANDARD_USERS_KEY] });
      },
    },
  );
  const { data: applicationTenants } = useApplicationTenants(
    companyUuid,
    APP_BASE_PATH,
    { enabled: open && !!companyUuid },
  );
  const { data: languages } = useLanguages(APP_BASE_PATH, { enabled: open });
  const { data: timezones } = useTimezones(APP_BASE_PATH, { enabled: open });

  const isAppAdmin = user?.userType === "APP_ADMIN";
  const activeRoleOptions = useMemo(() => {
    return isAppAdmin
      ? [
          {
            value: "app_admin",
            label: t("hub.administer.role_app_admin", {
              defaultValue: "App Admin",
            }),
          },
        ]
      : ROLE_OPTIONS;
  }, [isAppAdmin, t]);

  const defaultApplicationVersionId = useMemo(() => {
    if (!user?.applicationTenants?.[0] || !applicationTenants?.length)
      return "";
    const first = user.applicationTenants[0];
    const byVersion = applicationTenants.find(
      (a) => a.applicationVersionId === first.applicationVersionId,
    );
    if (byVersion) return byVersion.applicationVersionId;
    const tenantId = first.userTenants?.[0]?.tenantInstanceId;
    if (tenantId) {
      const byTenant = applicationTenants.find((a) =>
        a.tenantInfo?.some(
          (tenant: ITenantInstanceInfo) => tenant.tenantInstanceId === tenantId,
        ),
      );
      if (byTenant) return byTenant.applicationVersionId;
    }
    const byName = applicationTenants.find(
      (a) => a.applicationName === first.applicationName,
    );
    return byName?.applicationVersionId ?? "";
  }, [user, applicationTenants]);

  const defaultTenantIds = useMemo(() => {
    if (!user?.applicationTenants?.[0]?.userTenants) return [];
    return user.applicationTenants[0].userTenants
      .map((ut) => ut.tenantInstanceId)
      .filter((id): id is string => Boolean(id));
  }, [user]);

  const computedValues = useMemo<AddUserFormData>(() => {
    if (!user) {
      let autoApp = "";
      let autoTenants: string[] = [];

      const appOpts = buildApplicationOptions(applicationTenants);
      if (appOpts.length === 1) {
        autoApp = appOpts[0].value;
      }

      if (autoApp) {
        const tenantOpts = buildTenantOptions(applicationTenants, autoApp);
        if (tenantOpts.length === 1) {
          autoTenants = [tenantOpts[0].value];
        }
      }

      return {
        ...DEFAULT_ADD_USER_FORM_VALUES,
        application: autoApp,
        tenant: autoTenants,
      };
    }
    return {
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email ?? "",
      loginId: user.loginId ?? "",
      role: USER_TYPE_TO_ROLE[user.userType] ?? "standard",
      timezone: user.timezone || "UTC",
      language: user.language || "en-US",
      application: defaultApplicationVersionId,
      tenant: defaultTenantIds,
    };
  }, [user, defaultApplicationVersionId, defaultTenantIds, applicationTenants]);

  const form = useForm<AddUserFormData>({
    resolver: zodResolver(addUserFormSchema),
    mode: "onChange",
    defaultValues: DEFAULT_ADD_USER_FORM_VALUES,
    values: open ? computedValues : undefined,
  });

  const { control, handleSubmit, setValue, setError, clearErrors, reset } =
    form;

  useEffect(() => {
    if (!open) {
      reset(DEFAULT_ADD_USER_FORM_VALUES);
    }
  }, [open, reset]);

  const watchedEmail = useWatch({ control, name: "email" });
  const watchedLoginId = useWatch({ control, name: "loginId" });

  const debouncedValidation = useMemo(
    () =>
      debounce((email: string, loginId: string) => {
        checkLoginId({ email, loginId })
          .then((result) => {
            const errorMessage = t("hub.administer.add_user_login_id_exists", {
              defaultValue: "Login ID already exists.",
            });
            if (result.isExists) {
              setError("loginId", { type: "manual", message: errorMessage });
            } else {
              clearErrors(["loginId"]);
            }
          })
          .catch(() => {
            clearErrors(["loginId"]);
          });
      }, 400),
    [checkLoginId, setError, clearErrors, t],
  );

  useEffect(() => {
    if (isEdit || !open) return;
    const email = watchedEmail?.trim();
    const loginId = watchedLoginId?.trim();
    if (
      !companyUuid ||
      !email ||
      !loginId ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      clearErrors(["email", "loginId"]);
      return;
    }

    debouncedValidation(email, loginId);
  }, [
    isEdit,
    open,
    companyUuid,
    watchedEmail,
    watchedLoginId,
    debouncedValidation,
    clearErrors,
  ]);

  const applicationOptions = useMemo(
    () => buildApplicationOptions(applicationTenants),
    [applicationTenants],
  );

  const languageOptions = useMemo(
    () => withCurrentRegionalOption(languages, user?.language),
    [languages, user?.language],
  );

  const timezoneOptions = useMemo(
    () => withCurrentRegionalOption(timezones, user?.timezone),
    [timezones, user?.timezone],
  );

  const selectedApplication = useWatch({ control, name: "application" });
  const tenantOptions = useMemo(
    () => buildTenantOptions(applicationTenants, selectedApplication),
    [applicationTenants, selectedApplication],
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const onSubmit = useCallback(
    (data: AddUserFormData) => {
      if (onAddUser) {
        onAddUser(data);
        handleClose();
        return;
      }
      if (!companyUuid) return;
      if (isEdit && user) {
        const request = buildUpdateUserRequest(data, user, applicationTenants);
        if (request.applicationTenants.length === 0) return;
        updateUser(
          { userId: user.id, request },
          {
            onSuccess: () => {
              handleClose();
              toast.add({
                title: t("hub.administer.update_user_success"),
                description: t(
                  "hub.administer.update_user_success_description",
                ),
                data: { type: "success" },
              });
            },
            onError: (error: unknown) => {
              const apiError = error as {
                statusCode?: number;
                status?: number;
                message?: string;
              };
              toast.add({
                title: t("hub.administer.update_user_error"),
                description: t("hub.administer.update_user_error_description"),
                ...((apiError.statusCode === 409 || apiError.status === 409) &&
                apiError.message
                  ? { description: apiError.message }
                  : {}),
                data: { type: "error" },
              });
            },
          },
        );
      } else {
        if (!userInfo?.email) return;
        const request = buildAddUserRequest(
          data,
          companyUuid,
          userInfo.email,
          applicationTenants,
        );
        if (request.applicationTenants.length === 0) return;
        addUser(request, {
          onSuccess: () => {
            handleClose();
            toast.add({
              title: t("hub.administer.add_user_success"),
              description: t("hub.administer.add_user_success_description"),
              data: { type: "success" },
            });
          },
          onError: (error: unknown) => {
            const apiError = error as {
              statusCode?: number;
              status?: number;
              message?: string;
            };
            toast.add({
              title: t("hub.administer.add_user_error"),
              description: t("hub.administer.add_user_error_description"),
              ...((apiError.statusCode === 409 || apiError.status === 409) &&
              apiError.message
                ? { description: apiError.message }
                : {}),
              data: { type: "error" },
            });
          },
        });
      }
    },
    [
      onAddUser,
      companyUuid,
      isEdit,
      user,
      userInfo?.email,
      applicationTenants,
      addUser,
      updateUser,
      handleClose,
      toast,
      t,
    ],
  );

  const isPending = isAddUserPending || isUpdateUserPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl overflow-hidden flex flex-col max-h-[90vh]"
        size="lg">
        <DialogHeader className="shrink-0 mb-4">
          <DialogTitle className="text-base font-medium">
            {isEdit
              ? t("hub.administer.edit_user_dialog_title")
              : t("hub.administer.add_user_dialog_title")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex-1 overflow-y-auto min-h-0 px-2 py-1">
          <Form {...form}>
            <form
              id="add-user-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-6 text-left">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInputField<AddUserFormData>
                  control={control}
                  name="firstName"
                  label={t("hub.administer.first_name")}
                  placeholder={t("hub.administer.first_name_placeholder", {
                    defaultValue: "Enter first name",
                  })}
                  description={t("hub.administer.first_name_description", {
                    defaultValue: "User's given name.",
                  })}
                  required
                />
                <FormInputField<AddUserFormData>
                  control={control}
                  name="lastName"
                  label={t("hub.administer.last_name")}
                  placeholder={t("hub.administer.last_name_placeholder", {
                    defaultValue: "Enter last name",
                  })}
                  description={t("hub.administer.last_name_description", {
                    defaultValue: "User's family name.",
                  })}
                  required
                />
                <FormInputField<AddUserFormData>
                  control={control}
                  name="email"
                  label={t("hub.administer.email_id")}
                  type="email"
                  disabled={isEdit}
                  readOnly={isEdit}
                  required={!isEdit}
                  placeholder={
                    isEdit
                      ? undefined
                      : t("hub.administer.add_user_email_placeholder")
                  }
                  description={t("hub.administer.email_description", {
                    defaultValue: "User's email address.",
                  })}
                />
                <FormInputField<AddUserFormData>
                  control={control}
                  name="loginId"
                  label={t("hub.profile.login_id")}
                  disabled={isEdit}
                  readOnly={isEdit}
                  required={!isEdit}
                  placeholder={
                    isEdit
                      ? undefined
                      : t("hub.administer.add_user_login_placeholder")
                  }
                />
                <FormSelectField<AddUserFormData>
                  control={control}
                  name="role"
                  label={t("hub.administer.role")}
                  items={activeRoleOptions}
                  placeholder={t("hub.administer.add_user_select_placeholder")}
                  description={t("hub.administer.role_description", {
                    defaultValue: "Select the user's role.",
                  })}
                  required
                  disabled={isAppAdmin}
                />
                <FormSelectField<AddUserFormData>
                  control={control}
                  name="language"
                  label={t("hub.locale_switch.menu_label")}
                  items={languageOptions}
                  placeholder={t("hub.administer.add_user_select_placeholder")}
                  description={t("hub.administer.language_description", {
                    defaultValue: "Select the user's preferred language.",
                  })}
                  required
                />
                <FormSelectField<AddUserFormData>
                  control={control}
                  name="timezone"
                  label={t("hub.profile.timezone")}
                  items={timezoneOptions}
                  placeholder={t("hub.administer.add_user_select_placeholder")}
                  description={t("hub.administer.timezone_description", {
                    defaultValue: "Select the user's timezone.",
                  })}
                  required
                />
                <FormSelectField<AddUserFormData>
                  control={control}
                  name="application"
                  label={t("hub.administer.application")}
                  items={applicationOptions}
                  placeholder={t("hub.administer.add_user_select_placeholder")}
                  required
                  onValueChange={(v) => {
                    if (!v) {
                      setValue("tenant", [], { shouldValidate: true });
                      return;
                    }
                    const newTenantOpts = buildTenantOptions(
                      applicationTenants,
                      v,
                    );
                    if (newTenantOpts.length === 1) {
                      setValue("tenant", [newTenantOpts[0].value], {
                        shouldValidate: true,
                      });
                    } else {
                      setValue("tenant", [], { shouldValidate: true });
                    }
                  }}
                />
                <FormMultiSelect<AddUserFormData>
                  control={control}
                  name="tenant"
                  label={`${t("hub.administer.tenant")}(s)`}
                  items={tenantOptions}
                  placeholder={t("hub.administer.add_user_select_placeholder")}
                  description={t("hub.administer.tenant_description", {
                    defaultValue: "Select the tenant(s) for the user.",
                  })}
                />
              </div>
            </form>
          </Form>
        </DialogBody>
        <DialogFooter className="shrink-0 -mx-6 -mb-6 mt-2 border-t border-border bg-muted/50 p-4 gap-2 sm:gap-0 sm:flex-row sm:justify-end sm:space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("hub.administer.cancel")}
          </Button>
          <Button
            disabled={isPending}
            onClick={() => {
              const formElement = document.getElementById(
                "add-user-form",
              ) as HTMLFormElement;
              if (formElement) {
                formElement.requestSubmit();
              }
            }}>
            {isPending
              ? isEdit
                ? t("hub.administer.edit_user_submitting")
                : t("hub.administer.add_user_submitting")
              : isEdit
                ? t("hub.administer.save")
                : t("hub.administer.add_user_dialog_submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
